import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type UserCredits = { 
  credits: number;
};

/**
 * Centralized hook for fetching user credits.
 * Use this hook everywhere credits need to be displayed.
 * For premium status, use useProfile hook instead.
 */
export function useUserCredits(userId?: string | null) {
  return useQuery({
    queryKey: ["user-credits", userId],
    queryFn: async () => {
      if (!userId) {
        throw new Error("No user ID provided");
      }

      const { data, error } = await supabase
        .from("user_credits")
        .select("credits")
        .eq("user_id", userId)
        .single();

      if (error) {
        console.error("[useUserCredits] Error fetching credits:", error);
        throw error;
      }

      // Always return a stable shape and guard against NaN
      const creditsNum = Number(data?.credits ?? 0);
      const result = {
        credits: Number.isFinite(creditsNum) ? creditsNum : 0,
      } as UserCredits;
      
      console.log("[useUserCredits] Fetched credits:", { userId, credits: result.credits });
      return result;
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // Keep fresh for 2 minutes to balance freshness with performance
    refetchOnMount: false, // Don't refetch on mount if data is in cache
    refetchOnWindowFocus: false, // Don't refetch on focus to prevent flickering
    placeholderData: (previousData) => previousData, // Keep showing old credits while refetching
  });
}

/**
 * Helper function to format credits for display (divide by 10)
 */
export function formatCredits(credits: number): string {
  const val = credits / 10;
  if (!Number.isFinite(val)) return "0";
  return Number.isInteger(val) ? String(val) : val.toFixed(1);
}
